import React, {useState} from 'react';
import {
    faFireFlameCurved,
    faCalendarDay,
    faRotateLeft,
    faSquareCheck,
    faCircleCheck,
    faCircleXmark,
} from '@fortawesome/free-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {useSelector} from '@utils/hooks/common';
import {useStreakRecoveryStorage} from '@utils/hooks/useStreakRecoveryStorage';
import {t} from 'i18next';
import useScreenSize from '@utils/hooks/useScreenSize';
import {Link} from 'react-router';
import {MidnightCountdown} from '@components/common';
import {DailyProblemV2} from '@types';
import RankText from '@components/common/RankText';
import {ThemeAwareSkeleton} from '@components/theme/ThemeAwareSkeleton';
import {ThemeAwareBadge} from '@components/theme/ThemeAwareBadge';

const ProblemOfTheDayCard = ({
    dailyProblem,
}: {
    dailyProblem: DailyProblemV2;
}) => {
    const [imageLoaded, setImageLoaded] = useState(false);
    const {user} = useSelector(state => state.auth);
    const hasStatus =
        typeof dailyProblem.solvedByCurrentUser === 'boolean' &&
        typeof dailyProblem.solversCount === 'number';
    const hasAttempt =
        hasStatus &&
        (dailyProblem.solvedByCurrentUser ||
            dailyProblem.firstSolveAttemptFailedByCurrentUser);
    const resultIcon = dailyProblem.solvedByCurrentUser
        ? faCircleCheck
        : faCircleXmark;
    const streakLabel = t('index.potd.your_current_streak', {
        currentStreak: dailyProblem.currentStreak,
    });
    const statusLabel = dailyProblem.solvedByCurrentUser
        ? dailyProblem.firstSolveAttemptFailedByCurrentUser
            ? t('potd.solved_today')
            : t('potd.solvedFirst')
        : t('potd.not_solved_today');
    return (
        <div className="mb-2 min-w-0 w-1/2 px-1 site-sm:mb-0 site-sm:flex-1">
            <div
                className={`card index-home-feature-card index-home-potd-card relative ${
                    dailyProblem.solvedByCurrentUser
                        ? dailyProblem.firstSolveAttemptFailedByCurrentUser
                            ? 'border-success-subtle'
                            : 'border-success'
                        : ''
                }`}>
                <Link
                    to={`/problems/${dailyProblem.id}/?name=${dailyProblem.category}&dailyProblem=1`}
                    className="relative block aspect-square overflow-hidden text-decoration-none">
                    <span className="absolute inset-x-0 top-0 z-10 m-1 flex items-stretch justify-between gap-1">
                        <ThemeAwareBadge className="!inline-flex items-center justify-center">
                            <RankText rank={dailyProblem.difficulty} />
                        </ThemeAwareBadge>
                        {user && hasAttempt && (
                            <ThemeAwareBadge className="index-home-potd-board-status !inline-flex items-center justify-center">
                                <span
                                    className="inline-flex"
                                    role="img"
                                    aria-label={t(
                                        dailyProblem.solvedByCurrentUser
                                            ? 'common.correct'
                                            : 'common.incorrect'
                                    )}
                                    title={statusLabel}>
                                    <FontAwesomeIcon
                                        icon={resultIcon}
                                        className={
                                            dailyProblem.solvedByCurrentUser
                                                ? 'text-success'
                                                : 'text-red-600 dark:text-red-400'
                                        }
                                    />
                                </span>
                            </ThemeAwareBadge>
                        )}
                    </span>
                    {!imageLoaded && (
                        <ThemeAwareSkeleton
                            containerClassName="absolute inset-0"
                            style={{
                                width: '100%',
                                height: '100%',
                                borderRadius: 0,
                            }}
                        />
                    )}
                    <img
                        onLoad={() => setImageLoaded(true)}
                        fetchPriority="high"
                        className="card-img-top absolute inset-0 !h-full object-contain"
                        style={{display: imageLoaded ? 'block' : 'none'}}
                        src={dailyProblem.imageUrl}
                        alt={t('potd.problem_image')}
                    />
                </Link>
                <Link
                    to={`/problems/${dailyProblem.id}/potd-attempts`}>
                    <div className="card-body index-home-potd-footer">
                        <span
                            className="index-home-potd-category"
                            title={t(`index.potd.${dailyProblem.category}`)}>
                            {t(`index.potd.${dailyProblem.category}`)}
                        </span>
                        {hasStatus && (
                            <>
                                <span
                                    className="index-home-potd-status"
                                    role="img"
                                    aria-label={statusLabel}
                                    title={statusLabel}>
                                    <FontAwesomeIcon
                                        className={
                                            dailyProblem.solvedByCurrentUser
                                                ? dailyProblem.firstSolveAttemptFailedByCurrentUser
                                                    ? 'text-success-subtle'
                                                    : 'text-success'
                                                : dailyProblem.firstSolveAttemptFailedByCurrentUser
                                                  ? 'text-danger-subtle'
                                                  : 'text-light-gray'
                                        }
                                        icon={
                                            hasAttempt
                                                ? resultIcon
                                                : faCircleCheck
                                        }
                                    />
                                </span>
                                <div className="total-users-solved index-home-potd-stats">
                                    {user && !!dailyProblem.currentStreak && (
                                        <span
                                            className="index-home-potd-stat"
                                            title={streakLabel}
                                            aria-label={streakLabel}>
                                            <span>
                                                {dailyProblem.currentStreak}
                                            </span>
                                            <FontAwesomeIcon
                                                icon={faFireFlameCurved}
                                            />
                                        </span>
                                    )}
                                    <span
                                        className="index-home-potd-stat index-home-potd-solvers"
                                        title={t('potd.solverCount')}
                                        aria-label={`${t('potd.solverCount')}: ${dailyProblem.solversCount}`}>
                                        <span>
                                            {dailyProblem.solversCount}
                                        </span>
                                    </span>
                                </div>
                            </>
                        )}
                    </div>
                </Link>
            </div>
        </div>
    );
};

const ProblemOfTheDayCardSkeleton = () => {
    return (
        <div className="card index-home-feature-card index-home-potd-card">
            <div className="relative aspect-square">
                <ThemeAwareSkeleton
                    containerClassName="absolute inset-0"
                    style={{
                        width: '100%',
                        height: '100%',
                        borderRadius: 0,
                    }}
                />
            </div>
            <div className="card-body index-home-potd-footer" aria-hidden="true">
                <ThemeAwareSkeleton
                    containerClassName="index-home-potd-category"
                    width={60}
                    height={14}
                />
                <ThemeAwareSkeleton
                    containerClassName="index-home-potd-status"
                    width={14}
                    height={14}
                    circle
                />
                <div className="index-home-potd-stats">
                    <ThemeAwareSkeleton
                        containerClassName="index-home-potd-stat"
                        width={28}
                        height={10}
                    />
                    <ThemeAwareSkeleton
                        containerClassName="index-home-potd-stat index-home-potd-solvers"
                        width={32}
                        height={10}
                    />
                </div>
            </div>
        </div>
    );
};

const ProblemsOfTheDay: React.FC = () => {
    const {user} = useSelector(state => state.auth);
    const {width} = useScreenSize();
    const {
        recovery,
        activeRecovery,
        isRepaired,
        needsRepair,
        solvedToday,
        potdData: data,
        potdQuery: {isContentLoading},
    } = useStreakRecoveryStorage();

    const renderStreakIndicator = () => {
        if (typeof data?.currentStreak !== 'number') {
            return null;
        }
        const completedToday = solvedToday > 0 && !needsRepair;

        if (needsRepair && recovery) {
            const missedDays = recovery.requiredSolves - 1;
            const remaining = Math.max(
                0,
                recovery.requiredSolves - solvedToday
            );
            return (
                <Link
                    to={'/potd/streaks'}
                    className="index-home-heading-meta-link">
                    <div
                        className="d-flex flex-row align-items-center gap-1 text-yellow-500"
                        title={t('index.potd.streak_needs_repair', {
                            missedDays,
                            requiredSolves: recovery.requiredSolves,
                        })}>
                        <FontAwesomeIcon icon={faCalendarDay} />
                        <FontAwesomeIcon icon={faRotateLeft} size="xs" />
                        <span>{remaining}</span>
                        <FontAwesomeIcon icon={faFireFlameCurved} />
                        <span>{data?.currentStreak}</span>
                    </div>
                </Link>
            );
        }

        if (isRepaired) {
            return (
                <Link
                    to={'/potd/streaks'}
                    className="index-home-heading-meta-link">
                    <div
                        className="d-flex flex-row align-items-center gap-1 index-home-streak-complete"
                        title={t('index.potd.streak_repaired', {
                            currentStreak: data?.currentStreak,
                        })}>
                        <FontAwesomeIcon icon={faCalendarDay} />
                        <span>{activeRecovery?.requiredSolves}</span>
                        <FontAwesomeIcon icon={faFireFlameCurved} />
                        <span>{data?.currentStreak}</span>
                        <FontAwesomeIcon icon={faSquareCheck} aria-hidden="true" />
                    </div>
                </Link>
            );
        }

        return (
            <Link
                to={'/potd/streaks'}
                className="index-home-heading-meta-link">
                <div
                    className={`d-flex flex-row align-items-center gap-1${completedToday ? ' index-home-streak-complete' : ''}`}
                    title={t(
                        completedToday
                            ? 'daily_streaks.kept_today'
                            : 'index.current_daily_problem_streak'
                    )}>
                    <FontAwesomeIcon icon={faFireFlameCurved} />
                    {data?.currentStreak}
                    {completedToday && (
                        <FontAwesomeIcon
                            icon={faSquareCheck}
                            aria-hidden="true"
                        />
                    )}
                </div>
            </Link>
        );
    };

    return (
        <div className="index-home-feature">
            <div className="index-home-section-heading">
                <h2>{t('index.potd.title')}</h2>
                {user && (
                    <span className="index-home-heading-meta">
                        <MidnightCountdown
                            resetsAt={data?.resetsAt}
                        />
                        {renderStreakIndicator()}
                    </span>
                )}
            </div>
            <div
                className="problems-of-the-day -mx-1 flex flex-wrap"
                style={{flexWrap: width >= 576 ? 'nowrap' : 'wrap'}}>
                {isContentLoading
                    ? Array.from({length: 4}, (_, index) => (
                          <div
                              key={`skeleton-${index}`}
                              className="mb-2 w-1/2 px-1 site-sm:mb-0 site-sm:flex-1">
                              <ProblemOfTheDayCardSkeleton />
                          </div>
                      ))
                    : Array.isArray(data?.entries) &&
                      data.entries.map(dailyProblem => (
                          <ProblemOfTheDayCard
                              key={`${dailyProblem.category}-${dailyProblem.id}`}
                              dailyProblem={dailyProblem}
                          />
                      ))}
            </div>
        </div>
    );
};
export default ProblemsOfTheDay;
